import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
// authHeader removed: axios interceptor in App.js injects the token automatically.

const PlanTematicoModal = ({ open, onClose, onSaved }) => {
  const [step, setStep] = useState('form'); // form | generating | preview
  const [tema, setTema] = useState('');
  const [durationDays, setDurationDays] = useState(14);
  const [dificultad, setDificultad] = useState('intermedio');
  const [versiculosPerDay, setVersiculosPerDay] = useState('1-3');
  const [tono, setTono] = useState('devocional');
  const [denariosReward, setDenariosReward] = useState(200);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const reset = () => {
    setStep('form');
    setPreview(null);
    setTema('');
  };

  const handleGenerate = async (e) => {
    e?.preventDefault();
    if (!tema.trim()) {
      toast.error('Ingresá un tema');
      return;
    }
    setStep('generating');
    try {
      const res = await axios.post(
        `${API}/admin/bible-plans/generate-tematico`,
        {
          tema: tema.trim(),
          duration_days: Number(durationDays),
          dificultad,
          versiculos_per_day: versiculosPerDay,
          tono,
        },
        { timeout: 120000 }
      );
      setPreview(res.data);
      setStep('preview');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Error generando plan con AI';
      toast.error(msg);
      setStep('form');
    }
  };

  const updateDay = (idx, field, value) => {
    if (!preview) return;
    const newSchedule = [...preview.schedule];
    newSchedule[idx] = { ...newSchedule[idx], [field]: value };
    setPreview({ ...preview, schedule: newSchedule });
  };

  const updatePreviewField = (field, value) => {
    setPreview({ ...preview, [field]: value });
  };

  const handleSave = async () => {
    if (!preview) return;
    setSaving(true);
    try {
      const payload = {
        name: preview.name,
        description: preview.description,
        tipo: 'tematico',
        tema: preview.tema || tema,
        denarios_reward: Number(denariosReward),
        difficulty: preview.dificultad || dificultad,
        schedule: preview.schedule,
        duration_days: preview.schedule.length,
      };
      const res = await axios.post(`${API}/admin/bible-plans`, payload);
      toast.success('Plan temático guardado');
      onSaved?.(res.data);
      reset();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error guardando plan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3 overflow-y-auto" data-testid="plan-tematico-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-amber-500 to-orange-600 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <i className="fas fa-sparkles text-xl" />
            <div>
              <h2 className="text-lg font-bold">Plan Temático con AI</h2>
              <p className="text-xs opacity-90">Generado con Gemini 2.5 Pro</p>
            </div>
          </div>
          <button
            onClick={() => { reset(); onClose?.(); }}
            className="text-white/80 hover:text-white text-xl"
            data-testid="plan-tematico-close"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {step === 'form' && (
            <form onSubmit={handleGenerate} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Tema del plan <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={tema}
                  onChange={(e) => setTema(e.target.value)}
                  placeholder="Ej: El perdón, La fe, Liderazgo bíblico, Identidad en Cristo..."
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  data-testid="plan-tematico-tema"
                  maxLength={120}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Duración (días)</label>
                  <select
                    value={durationDays}
                    onChange={(e) => setDurationDays(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    data-testid="plan-tematico-duration"
                  >
                    {[3, 5, 7, 10, 14, 21, 30, 40, 60, 90].map((n) => (
                      <option key={n} value={n}>{n} días</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Recompensa (denarios)</label>
                  <input
                    type="number"
                    min="0"
                    max="5000"
                    value={denariosReward}
                    onChange={(e) => setDenariosReward(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    data-testid="plan-tematico-denarios"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Dificultad</label>
                  <select
                    value={dificultad}
                    onChange={(e) => setDificultad(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    data-testid="plan-tematico-dificultad"
                  >
                    <option value="principiante">Principiante</option>
                    <option value="intermedio">Intermedio</option>
                    <option value="avanzado">Avanzado</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Tono</label>
                  <select
                    value={tono}
                    onChange={(e) => setTono(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    data-testid="plan-tematico-tono"
                  >
                    <option value="devocional">Devocional</option>
                    <option value="estudio">Estudio profundo</option>
                    <option value="jovenes">Jóvenes</option>
                    <option value="ninos">Niños / Adolescentes</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Lectura por día</label>
                  <div className="flex gap-2">
                    {[
                      { val: '1', label: 'Un versículo' },
                      { val: '1-3', label: '1-3 versículos' },
                      { val: 'capitulo', label: 'Capítulo completo' },
                    ].map((o) => (
                      <button
                        key={o.val}
                        type="button"
                        onClick={() => setVersiculosPerDay(o.val)}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition ${
                          versiculosPerDay === o.val
                            ? 'bg-amber-600 text-white border-amber-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-amber-400'
                        }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                <i className="fas fa-info-circle mr-2" />
                Gemini generará un plan temático con referencias bíblicas reales. Después podrás <strong>revisarlo y editarlo</strong> antes de guardar. Los estudiantes que lo completen ganarán los denarios definidos.
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => { reset(); onClose?.(); }}
                  className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg font-semibold hover:shadow-lg flex items-center gap-2"
                  data-testid="plan-tematico-generate-btn"
                >
                  <i className="fas fa-sparkles" />
                  Generar con AI
                </button>
              </div>
            </form>
          )}

          {step === 'generating' && (
            <div className="py-16 text-center">
              <div className="inline-block w-12 h-12 border-4 border-amber-200 border-t-amber-600 rounded-full animate-spin mb-4" />
              <p className="text-gray-700 font-semibold">Generando plan con Gemini 2.5 Pro...</p>
              <p className="text-sm text-gray-500 mt-2">Esto puede tardar 20-60 segundos</p>
            </div>
          )}

          {step === 'preview' && preview && (
            <div className="space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800 flex items-start gap-2">
                <i className="fas fa-check-circle mt-0.5" />
                <div>
                  <strong>Plan generado.</strong> Revisá y editá lo que quieras antes de guardar.
                  {preview.invalid_references_count > 0 && (
                    <p className="text-amber-700 mt-1">
                      ⚠️ {preview.invalid_references_count} referencia(s) no se pudo validar contra YouVersion.
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Nombre del plan</label>
                <input
                  type="text"
                  value={preview.name}
                  onChange={(e) => updatePreviewField('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-semibold"
                  data-testid="plan-tematico-preview-name"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Descripción</label>
                <textarea
                  value={preview.description}
                  onChange={(e) => updatePreviewField('description', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  data-testid="plan-tematico-preview-description"
                />
              </div>

              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-2">Cronograma ({preview.schedule.length} días)</h3>
                <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
                  {preview.schedule.map((day, idx) => (
                    <div
                      key={day.day}
                      className={`p-3 rounded-lg border ${day.reference_valid !== false ? 'border-gray-200 bg-gray-50' : 'border-amber-300 bg-amber-50'}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-7 h-7 bg-amber-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {day.day}
                        </span>
                        <input
                          type="text"
                          value={day.title || ''}
                          onChange={(e) => updateDay(idx, 'title', e.target.value)}
                          placeholder="Título del día"
                          className="flex-1 px-2 py-1 border border-gray-200 rounded text-sm font-medium"
                        />
                        <input
                          type="text"
                          value={day.reading || ''}
                          onChange={(e) => updateDay(idx, 'reading', e.target.value)}
                          placeholder="Ej: Juan 3:16"
                          className="w-44 px-2 py-1 border border-gray-200 rounded text-sm font-mono"
                          title="Referencia bíblica"
                        />
                      </div>
                      <textarea
                        value={day.reflection || ''}
                        onChange={(e) => updateDay(idx, 'reflection', e.target.value)}
                        rows={2}
                        placeholder="Reflexión del día"
                        className="w-full px-2 py-1 border border-gray-200 rounded text-xs text-gray-700"
                      />
                      {day.reference_usfm && (
                        <p className="text-[10px] text-gray-400 mt-1 font-mono">USFM: {day.reference_usfm}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-2 sticky bottom-0 bg-white pb-1">
                <button
                  onClick={() => setStep('form')}
                  className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  disabled={saving}
                >
                  ← Regenerar
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-5 py-2 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-lg font-semibold hover:shadow-lg flex items-center gap-2 disabled:opacity-60"
                  data-testid="plan-tematico-save-btn"
                >
                  {saving ? (
                    <><i className="fas fa-spinner fa-spin" /> Guardando...</>
                  ) : (
                    <><i className="fas fa-save" /> Guardar Plan</>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanTematicoModal;
